import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";

import ProcesosApi from "api/procesosApi";
import DatosBasicosApi from "api/datosbasicosApi";

const EditarAudiencia = ({ open, onClose, audienciaId, audiencia: audienciaProp, onSuccess }) => {
  const [error, setError] = useState(undefined);
  const [audiencia, setAudiencia] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [parametroZoom, setParametroZoom] = useState(null);
  const [zoomAudiencia, setZoomAudiencia] = useState("");
  const [salasZoom, setSalasZoom] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      // Si se pasa la audiencia como prop, usarla directamente
      if (audienciaProp) {
        setAudiencia(audienciaProp);
        return;
      }
      // Solo hacer fetch si no se pasa la audiencia como prop
      const audienciaResp = await ProcesosApi.fetchAudienciaById(audienciaId);
      setAudiencia(audienciaResp.data);
    } catch (error) {
      console.error("Error al obtener los datos iniciales:", error);
      setError(obtenerMensajeError(error, "Error al cargar los datos iniciales."));
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener el parámetro zoom basado en id_zoom
  const fetchParametroZoom = async (idZoom) => {
    try {
      if (idZoom) {
        const respParametro = await DatosBasicosApi.fetchParametroByValor(idZoom);
        setParametroZoom(respParametro.data);
        const zoom_audiencia = respParametro.data.nombre.substring(3);
        setZoomAudiencia(zoom_audiencia);
      }
    } catch (error) {
      console.error("Error al obtener el parámetro zoom:", error);
      setParametroZoom(null);
    }
  };

  const fetchSalasZoom = async () => {
    try {
      const salasZoomResp = await DatosBasicosApi.fetchSalasZoomActivas();
      setSalasZoom(Array.isArray(salasZoomResp.data) ? salasZoomResp.data : []);
    } catch (error) {
      console.error("Error al obtener las salas Zoom:", error);
      setError(obtenerMensajeError(error, "Error al cargar las Salas Zoom."));
    }
  };

  useEffect(() => {
    if (open && (audienciaId || audienciaProp)) {
      fetchInitialData();
      fetchSalasZoom();
    }
  }, [audienciaId, audienciaProp, open]);

  // Actualizar audiencia local cuando cambie la prop y obtener parámetro zoom
  useEffect(() => {
    if (audienciaProp) {
      setAudiencia(audienciaProp);
      // Si la audiencia tiene id_zoom, obtener el parámetro correspondiente
      if (audienciaProp.id_zoom) {
        fetchParametroZoom(audienciaProp.id_zoom);
      }
    }
  }, [audienciaProp]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validaciones
    if (!audiencia?.fecha) {
      alert("Debe seleccionar una Fecha para la audiencia.");
      return;
    }
    if (!audiencia?.hora) {
      alert("Debe ingresar una Hora para la audiencia.");
      return;
    }
    if (audiencia?.modalidad === "Virtual" && !zoomAudiencia) {
      alert("Debe seleccionar un Zoom para la audiencia virtual.");
      return;
    }
    if (audiencia?.modalidad === "Presencial" && !audiencia?.lugar) {
      alert("Debe ingresar el lugar para la audiencia presencial.");
      return;
    }
    const confirmacion = window.confirm(`¿Está seguro(a) de que desea actualizar la Audiencia?`);
    if (!confirmacion) return;
    try {
      setSaving(true);
      const updatedAudiencia = {
        ...audiencia,
        zoom_audiencia: zoomAudiencia,
      };
      console.log("Guardando audiencia actualizada:", updatedAudiencia);
      const response = await ProcesosApi.actualizarAudiencia(audiencia?.id, updatedAudiencia);
      if (response.status === 200) {
        alert("La Audiencia ha sido actualizada correctamente.");
        setError(undefined);
        onSuccess();
      }
    } catch (error) {
      console.error("Error al Actualizar la Audiencia:", error.response?.data?.error);
      setError(obtenerMensajeError(error, "Ocurrió un error al Actualizar la Audiencia."));
    } finally {
      setSaving(false);
    }
  };

  const hCClose = () => {
    setError(undefined);
    setParametroZoom(null);
    onClose();
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={hCClose} maxWidth="md" fullWidth>
        <DialogContent>
          <SuiBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </SuiBox>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      {saving && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Dialog open={open} onClose={hCClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <SuiBox mt={1}>
            <strong>
              Actualizar Audiencia No. {audiencia?.consecutivo} del Proceso:{" "}
              {audiencia?.proceso_identificador || "No tiene Proceso"}
            </strong>
          </SuiBox>
        </DialogTitle>
        <DialogContent>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Grid>
          </Grid>

          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 2 }}>
            <Grid size={12}>
              <SuiTypography variant="h6" fontWeight="light">
                Fecha y Hora
              </SuiTypography>
            </Grid>
            <Grid size={6}>
              <SuiInput
                fullWidth
                type="date"
                value={audiencia?.fecha || ""}
                onChange={(e) => setAudiencia({ ...audiencia, fecha: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={6}>
              <SuiInput
                fullWidth
                type="time"
                value={audiencia?.hora || ""}
                onChange={(e) => setAudiencia({ ...audiencia, hora: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
          </Grid>

          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 2 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Modalidad
              </SuiTypography>
              <Select
                value={audiencia?.modalidad || ""}
                onChange={(e) => {
                  const newModalidad = e.target.value;
                  setAudiencia({
                    ...audiencia,
                    modalidad: newModalidad,
                    // Limpiar campos relacionados al cambiar modalidad
                    // ...(newModalidad === "Presencial" && {
                    //   zoom_audiencia: "",
                    //   enlace_zoom: "",
                    //   id_zoom: "",
                    // }),
                    // ...(newModalidad === "Virtual" && { lugar: "" }),
                  });
                }}
                fullWidth
                displayEmpty
              >
                <MenuItem value="">
                  <em>Seleccione una Modalidad</em>
                </MenuItem>
                <MenuItem value="Presencial">Presencial</MenuItem>
                <MenuItem value="Virtual">Virtual</MenuItem>
              </Select>
            </Grid>

            {audiencia?.modalidad === "Virtual" && (
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="light">
                  Zoom
                </SuiTypography>
                <Select
                  value={zoomAudiencia || ""}
                  onChange={(e) => {
                    setZoomAudiencia(e.target.value);
                  }}
                  fullWidth
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Seleccione un Zoom</em>
                  </MenuItem>
                  {salasZoom.map((sala) => (
                    <MenuItem key={sala.codigo} value={sala.codigo}>
                      {sala.nombre}
                    </MenuItem>
                  ))}
                  {/* Sala asignada a la audiencia que fue inactivada en Configuración */}
                  {zoomAudiencia && !salasZoom.some((sala) => sala.codigo === zoomAudiencia) && (
                    <MenuItem value={zoomAudiencia}>
                      {zoomAudiencia.replace("_", " ")} (inactiva)
                    </MenuItem>
                  )}
                </Select>
              </Grid>
            )}

            {audiencia?.modalidad === "Presencial" && (
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="light">
                  Lugar
                </SuiTypography>
                <SuiInput
                  placeholder="Lugar de la Audiencia Presencial"
                  value={audiencia?.lugar || ""}
                  onChange={(e) => {
                    setAudiencia({ ...audiencia, lugar: e.target.value });
                  }}
                  sx={{ flex: 1 }}
                />
              </Grid>
            )}
          </Grid>

          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              <SuiTypography variant="h6" fontWeight="light">
                Observación
              </SuiTypography>
              <SuiInput
                multiline
                rows={4}
                value={audiencia?.observaciones || ""}
                onChange={(e) => setAudiencia({ ...audiencia, observaciones: e.target.value })}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <SuiButton onClick={hCClose} color="secondary">
            Cancelar
          </SuiButton>
          <SuiButton
            variant="gradient"
            buttonColor="dark"
            onClick={handleSubmit}
            disabled={saving || loading}
          >
            {saving ? "Actualizando..." : "Actualizar"}
          </SuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

EditarAudiencia.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  audienciaId: PropTypes.number,
  audiencia: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
};

export default EditarAudiencia;
